import React, { useState, useEffect, useMemo } from 'react'
import { TimeSeriesDecomposition, ModelMetrics, Prediction } from '../../types/ml'
import TimeSeriesDecompositionChart from '../../components/charts/TimeSeriesDecompositionChart'
import ForecastChart from '../../components/charts/ForecastChart'
import BacktestChart from '../../components/charts/BacktestChart'
import ACFChart from '../../components/charts/ACFChart'

const TimeSeries: React.FC = () => {
  const [selectedSystem, setSelectedSystem] = useState('INV-01')
  const [selectedModel, setSelectedModel] = useState('prophet')
  const [horizon, setHorizon] = useState('168') // hours
  const [decomposition, setDecomposition] = useState<TimeSeriesDecomposition | null>(null)
  const [forecast, setForecast] = useState<Prediction | null>(null)
  const [modelComparison, setModelComparison] = useState<Record<string, ModelMetrics>>({})
  const [seasonalityDetected, setSeasonalityDetected] = useState<string[]>([])

  const mockDecomposition = useMemo(() => {
    const length = 168
    return {
      timestamps: Array.from({ length }, (_, i) => 
        new Date(Date.now() - (length - i) * 3600000).toISOString()
      ),
      trend: Array.from({ length }, (_, i) => 30 + Math.sin(i / 24) * 5),
      seasonal: Array.from({ length }, (_, i) => Math.sin(i * 2 * Math.PI / 24) * 10),
      residual: Array.from({ length }, () => (Math.random() - 0.5) * 4)
    }
  }, []);

  const mockForecast = useMemo(() => {
    const hours = parseInt(horizon)
    return {
      timestamps: Array.from({ length: hours }, (_, i) => 
        new Date(Date.now() + i * 3600000).toISOString()
      ),
      y_pred: Array.from({ length: hours }, (_, i) => 
        25 + Math.sin(i * 2 * Math.PI / 24) * 15 + (i * 0.1)
      ),
      lower_95: Array.from({ length: hours }, (_, i) => 
        20 + Math.sin(i * 2 * Math.PI / 24) * 12
      ),
      upper_95: Array.from({ length: hours }, (_, i) => 
        30 + Math.sin(i * 2 * Math.PI / 24) * 18
      ),
      model_version: selectedModel
    }
  }, [horizon, selectedModel]);

  useEffect(() => {
    setDecomposition(mockDecomposition)
    setForecast(mockForecast)
    fetchModelComparison()
    detectSeasonality()
  }, [mockDecomposition, mockForecast])



  const fetchModelComparison = async () => {
    const mockComparison = {
      'ARIMA': { rmse: 3.45, mae: 2.67, mape: 8.2, r_squared: 0.87 },
      'Prophet': { rmse: 2.89, mae: 2.23, mape: 6.8, r_squared: 0.91 },
      'ETS': { rmse: 4.12, mae: 3.15, mape: 9.7, r_squared: 0.82 },
      'Ensemble': { rmse: 2.56, mae: 1.98, mape: 5.9, r_squared: 0.93 }
    }
    setModelComparison(mockComparison)
  }

  const detectSeasonality = async () => {
    // Mock seasonality detection
    setSeasonalityDetected(['Daily (24h)', 'Weekly (168h)', 'Monthly (720h)'])
  }

  const exportModel = () => {
    // Mock export functionality
    const blob = new Blob(['# Model Export\nmodel_params = {...}'], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${selectedModel}_${selectedSystem}.py`
    a.click()
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header & Controls */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Time Series Analysis</h1>
        <div className="flex gap-4">
          <select 
            value={selectedSystem} 
            onChange={(e) => setSelectedSystem(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="INV-01">INV-01</option>
            <option value="INV-02">INV-02</option>
            <option value="PLANT-01">Plant Total</option>
          </select>
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="prophet">Prophet</option>
            <option value="arima">ARIMA</option>
            <option value="ets">ETS</option>
            <option value="ensemble">Ensemble</option>
          </select>
          <select 
            value={horizon} 
            onChange={(e) => setHorizon(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="24">24 Hours</option>
            <option value="168">1 Week</option>
            <option value="720">1 Month</option>
          </select>
          <button 
            onClick={exportModel}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Export Model
          </button>
        </div>
      </div>

      {/* Seasonality Detection */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Detected Seasonality Patterns</h3>
        <div className="flex gap-4">
          {seasonalityDetected.map((pattern, index) => (
            <div key={index} className="bg-blue-100 dark:bg-blue-900 px-3 py-1 rounded-full text-sm">
              {pattern}
            </div>
          ))}
        </div>
      </div>

      {/* Series Decomposition */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Time Series Decomposition</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <h4 className="text-sm font-medium text-slate-600 mb-2">Trend 📈</h4>
            <TimeSeriesDecompositionChart data={decomposition} component="trend" />
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-slate-600 mb-2">Seasonal 🔄</h4>
            <TimeSeriesDecompositionChart data={decomposition} component="seasonal" />
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-slate-600 mb-2">Residual 📊</h4>
            <TimeSeriesDecompositionChart data={decomposition} component="residual" />
          </div>
        </div>
      </div>

      {/* Forecast Chart */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Forecast ({selectedModel.toUpperCase()}) 📊
        </h3>
        <ForecastChart data={forecast} />
        <p className="text-sm text-slate-500 mt-2">
          {forecast ? `${forecast.y_pred.length} hour forecast` : 'No data'}
        </p>
      </div>

      {/* Model Comparison & Diagnostics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Comparison */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Model Comparison</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2">Model</th>
                  <th className="text-left py-2">RMSE</th>
                  <th className="text-left py-2">MAE</th>
                  <th className="text-left py-2">MAPE</th>
                  <th className="text-left py-2">R²</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(modelComparison).map(([model, metrics]) => (
                  <tr key={model} className={`border-b border-gray-100 dark:border-gray-700 ${
                    model === selectedModel ? 'bg-blue-50 dark:bg-blue-900' : ''
                  }`}>
                    <td className="py-2 font-medium">{model}</td>
                    <td className="py-2">{metrics.rmse.toFixed(2)}</td>
                    <td className="py-2">{metrics.mae.toFixed(2)}</td>
                    <td className="py-2">{metrics.mape.toFixed(1)}%</td>
                    <td className="py-2">{metrics.r_squared.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Residual Diagnostics */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Residual Diagnostics</h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-600 mb-2">
                Autocorrelation Function (ACF) 📈
              </h4>
              <ACFChart type="acf" />
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-slate-600 mb-2">
                Partial Autocorrelation (PACF) 📊
              </h4>
              <ACFChart type="pacf" />
            </div>
            
            {/* Statistical Tests */}
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="text-sm font-medium mb-2">Statistical Tests</h4>
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Ljung-Box p-value:</span>
                  <span className="text-green-600">0.342</span>
                </div>
                <div className="flex justify-between">
                  <span>Jarque-Bera p-value:</span>
                  <span className="text-green-600">0.156</span>
                </div>
                <div className="flex justify-between">
                  <span>ADF Stationarity:</span>
                  <span className="text-green-600">Stationary</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forecast Performance */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Backtest Results 📊
        </h3>
        <BacktestChart />
        <p className="text-sm text-slate-500 mt-2">Rolling Window Cross-Validation Results</p>
      </div>
    </div>
  )
}

export default TimeSeries